import { Check, CircleHelp, X } from 'lucide-react';

import { cn } from '@/design-system/lib/cn';
import { Button } from '@/design-system/ui/Button';
import { PERIOD_HINT, PERIOD_LABEL, WEEKDAY_LONG } from '@escala/domain/dates';
import { availIndex } from '@escala/domain/schedule';
import type { AvailState, Period } from '@escala/domain/types';

const CELL: Record<AvailState, { label: string; icon: typeof Check; className: string }> = {
  available: {
    label: 'Disponível',
    icon: Check,
    className: 'border-status-complete-line bg-status-complete-bg text-status-complete',
  },
  unavailable: {
    label: 'Indisponível',
    icon: X,
    className: 'border-status-conflict-line bg-status-conflict-bg text-status-conflict',
  },
  unset: {
    label: 'Não configurado',
    icon: CircleHelp,
    className: 'border-dashed border-line-dashed bg-white text-ink-subtle',
  },
};

const PERIODS: Period[] = [0, 1, 2];

export type AvailabilityGridProps = {
  /** As 21 posições: `weekday * 3 + period`. */
  value: readonly AvailState[];
  editable: boolean;
  /** Um período, a linha ou a coluna; `value` força o valor (os atalhos). */
  onSet: (indexes: number[], value?: AvailState) => void;
};

/**
 * A grade da disponibilidade semanal (7 dias × manhã/tarde/noite), com os atalhos e a
 * legenda: cada célula alterna Disponível › Indisponível › Não configurado; o dia ou o
 * período alternam a linha ou a coluna inteira. Usada no perfil (aba Disponibilidade) e no
 * "Aplicar disponibilidade" do Editor de Membros.
 */
export function AvailabilityGrid({ value: av, editable, onSet }: AvailabilityGridProps) {
  const row = (day: number) => PERIODS.map((p) => availIndex(day, p));
  const column = (p: Period) => WEEKDAY_LONG.map((_, day) => availIndex(day, p));
  const everything = av.map((_, i) => i);

  const presets: { label: string; apply: () => void }[] = [
    { label: 'Tudo disponível', apply: () => onSet(everything, 'available') },
    {
      label: 'Só domingos',
      apply: () => {
        onSet(
          everything.filter((i) => i >= 3),
          'unavailable'
        );
        onSet(row(0), 'available');
      },
    },
    { label: 'Limpar tudo', apply: () => onSet(everything, 'unset') },
  ];

  return (
    <>
      {editable ? (
        <div className="flex flex-wrap gap-1.5">
          {presets.map((p) => (
            <Button key={p.label} size="xs" variant="secondary" onClick={p.apply}>
              {p.label}
            </Button>
          ))}
        </div>
      ) : null}

      <div
        aria-label="Matriz de disponibilidade"
        role="group"
        className="grid grid-cols-[minmax(64px,auto)_repeat(3,minmax(0,1fr))] gap-1.5"
      >
        <span />
        {PERIODS.map((p) => (
          <button
            key={p}
            type="button"
            disabled={!editable}
            onClick={() => onSet(column(p))}
            title={`Alternar ${PERIOD_LABEL[p].toLowerCase()} em todos os dias`}
            className="flex flex-col items-center rounded-control-sm py-1 leading-tight enabled:hover:bg-surface-subtle"
          >
            <span className="text-control font-semibold">{PERIOD_LABEL[p]}</span>
            <span className="text-micro text-ink-subtle">{PERIOD_HINT[p]}</span>
          </button>
        ))}
        {WEEKDAY_LONG.map((day, d) => (
          <div key={day} className="contents">
            <button
              type="button"
              disabled={!editable}
              onClick={() => onSet(row(d))}
              title={`Alternar ${day.toLowerCase()} inteiro`}
              className="flex items-center rounded-control-sm px-1.5 text-left text-control font-semibold enabled:hover:bg-surface-subtle"
            >
              <span className="md:hidden">{day.slice(0, 3)}</span>
              <span className="max-md:hidden">{day}</span>
            </button>
            {PERIODS.map((p) => {
              const value = av[availIndex(d, p)] ?? 'unset';
              const cell = CELL[value];
              const Icon = cell.icon;

              return (
                <button
                  key={p}
                  type="button"
                  disabled={!editable}
                  onClick={() => onSet([availIndex(d, p)])}
                  aria-label={`${day} · ${PERIOD_LABEL[p]}: ${cell.label}`}
                  className={cn(
                    'flex h-11.5 items-center justify-center gap-1.5 rounded-control border text-small font-medium transition-colors',
                    cell.className
                  )}
                >
                  <Icon aria-hidden className="size-4 stroke-2" />
                  <span className="max-lg:hidden">{cell.label}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-4.5 border-t border-line-divider pt-3 text-meta text-ink-muted">
        {(Object.keys(CELL) as AvailState[]).map((s) => {
          const Icon = CELL[s].icon;

          return (
            <span key={s} className="inline-flex items-center gap-2">
              <span
                aria-hidden
                className={cn('grid h-4.5 w-6 place-items-center rounded-chip border', CELL[s].className)}
              >
                <Icon className="size-3 stroke-2" />
              </span>
              {CELL[s].label}
            </span>
          );
        })}
      </div>
    </>
  );
}
